import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../hooks/useAuth";
import { motion } from "framer-motion";
import {
  ChevronDownIcon,
  ImagePlusIcon,
  PencilIcon,
  SaveIcon,
  Trash2Icon,
  UserPlusIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";

export default function MyPlayers() {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [name, setName] = useState("");
  const [handicap, setHandicap] = useState("");
  const [club, setClub] = useState("");
  const [picture, setPicture] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [expandedPlayerId, setExpandedPlayerId] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 640
  );

  useEffect(() => {
    if (user) fetchPlayers();
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 640);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchPlayers = async () => {
    const { data, error } = await supabase
      .from("players")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Kunde inte hämta spelare", error);
    } else {
      setPlayers(data || []);
    }
  };

  const handleAddOrUpdatePlayer = async () => {
    if (!name || !handicap) {
      alert("Fyll i namn och hcp");
      return;
    }

    const playerData = {
      name,
      handicap: parseFloat(handicap),
      home_club: club,
      image_yrl: picture || null,
      user_id: user.id,
    };

    if (editingId) {
      const { error } = await supabase
        .from("players")
        .update(playerData)
        .eq("id", editingId);

      if (error) {
        console.error("Kunde inte uppdatera spelare", error);
        alert("Misslyckades med att spara ändringar.");
        return;
      }
    } else {
      const { error } = await supabase.from("players").insert([playerData]);

      if (error) {
        console.error("Kunde inte spara spelare", error);
        alert("Misslyckades med att spara spelare.");
        return;
      }
    }

    resetForm();
    fetchPlayers();
  };

  const handlePictureChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const filePath = `${user.id}/${file.name}`;

    const { error: uploadError } = await supabase.storage
      .from("player-images")
      .upload(filePath, file);

    if (uploadError) {
      alert("Kunde inte ladda upp bild");
      console.error("Upload error:", uploadError);
      return;
    }

    const { data: urlData } = supabase.storage
      .from("player-images")
      .getPublicUrl(filePath);

    setPicture(urlData.publicUrl);
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;

    const { error } = await supabase
      .from("players")
      .delete()
      .eq("id", deleteCandidate.id);

    if (error) {
      console.error("Kunde inte ta bort spelare", error);
      alert("Något gick fel.");
      return;
    }

    setDeleteCandidate(null);
    fetchPlayers();
  };

  const handleEdit = (player) => {
    setEditingId(player.id);
    setName(player.name);
    setHandicap(player.handicap);
    setClub(player.home_club || "");
    setPicture(player.image_url || player.image_yrl || "");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setHandicap("");
    setClub("");
    setPicture("");
  };

  const getInitials = (playerName = "") => {
    const initials = playerName
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("");

    return initials || "?";
  };

  return (
    <div className="min-h-screen bg-[#f3f0e7] text-[#1f2d26]">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-32 -top-28 h-96 w-96 rounded-full bg-[#dfe9e1] blur-3xl" />
        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#eee2c8] blur-3xl" />
      </div>

      <main className="relative mx-auto w-full max-w-5xl px-4 py-7 sm:px-6 sm:py-10">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          {/* Page header */}
          <header className="relative overflow-hidden rounded-[26px] bg-[#183d2d] px-5 py-6 text-white shadow-[0_18px_50px_rgba(24,61,45,0.16)] sm:px-7 sm:py-7">
            <div className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-[#b08b45]/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -left-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />

            <div className="relative flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/55">
                  Spelarregister
                </p>

                <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">
                  Hantera spelare
                </h1>

                <p className="mt-2 max-w-xl text-sm leading-6 text-white/65">
                  Lägg till spelare och håll handicap, klubb och profilbild
                  uppdaterade.
                </p>
              </div>

              <span className="self-start rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium backdrop-blur-sm sm:self-auto">
                {players.length} {players.length === 1 ? "spelare" : "spelare"}
              </span>
            </div>
          </header>

          <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
            {/* Add/edit form */}
            <section className="h-fit overflow-hidden rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_8px_24px_rgba(31,45,38,0.07)] lg:sticky lg:top-5">
              <div className="border-b border-[#e4ded1] px-4 py-4 sm:px-5">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e5ede7] text-[#183d2d]">
                    {editingId ? (
                      <PencilIcon className="h-5 w-5" />
                    ) : (
                      <UserPlusIcon className="h-5 w-5" />
                    )}
                  </span>

                  <div>
                    <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9a7735]">
                      {editingId ? "Redigeringsläge" : "Ny spelare"}
                    </p>

                    <h2 className="mt-0.5 text-lg font-semibold text-[#183d2d]">
                      {editingId ? "Redigera spelare" : "Lägg till spelare"}
                    </h2>
                  </div>
                </div>
              </div>

              <div className="space-y-4 p-4 sm:p-5">
                <div>
                  <label
                    htmlFor="player-name"
                    className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.15em] text-[#68716c]"
                  >
                    Namn
                  </label>

                  <input
                    id="player-name"
                    type="text"
                    placeholder="För- och efternamn"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    className="w-full rounded-xl border border-[#b8b09f] bg-[#fffdf8] px-3 py-2.5 text-sm text-[#1f2d26] outline-none placeholder:text-[#9a9f9b] focus:ring-2 focus:ring-[#183d2d]/15"
                  />
                </div>

                <div className="grid grid-cols-[100px_minmax(0,1fr)] gap-3">
                  <div>
                    <label
                      htmlFor="player-handicap"
                      className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.15em] text-[#68716c]"
                    >
                      Handicap
                    </label>

                    <input
                      id="player-handicap"
                      type="number"
                      step="0.1"
                      placeholder="HCP"
                      value={handicap}
                      onChange={(event) => setHandicap(event.target.value)}
                      className="w-full rounded-xl border border-[#b8b09f] bg-[#fffdf8] px-3 py-2.5 text-center text-sm font-semibold text-[#1f2d26] outline-none placeholder:text-[#9a9f9b] focus:ring-2 focus:ring-[#183d2d]/15"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="player-club"
                      className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.15em] text-[#68716c]"
                    >
                      Klubb
                    </label>

                    <input
                      id="player-club"
                      type="text"
                      placeholder="Hemmaklubb"
                      value={club}
                      onChange={(event) => setClub(event.target.value)}
                      className="w-full rounded-xl border border-[#b8b09f] bg-[#fffdf8] px-3 py-2.5 text-sm text-[#1f2d26] outline-none placeholder:text-[#9a9f9b] focus:ring-2 focus:ring-[#183d2d]/15"
                    />
                  </div>
                </div>

                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#68716c]">
                    Profilbild
                  </p>

                  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-[#b8b09f] bg-[#f7f4ec] px-3 py-3 transition hover:border-[#9a7735] hover:bg-[#f4efe4]">
                    {picture ? (
                      <img
                        src={picture}
                        alt="Förhandsvisning"
                        className="h-11 w-11 shrink-0 rounded-full border border-[#d8d2c4] object-cover"
                      />
                    ) : (
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e5ede7] text-[#183d2d]">
                        <ImagePlusIcon className="h-5 w-5" />
                      </span>
                    )}

                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-[#183d2d]">
                        {picture ? "Byt bild" : "Ladda upp bild"}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-[#7b817d]">
                        Välj en bild från din enhet
                      </span>
                    </span>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePictureChange}
                      className="sr-only"
                    />
                  </label>
                </div>

                <div className="flex flex-col gap-2 pt-1 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleAddOrUpdatePlayer}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#214d39]"
                  >
                    <SaveIcon className="h-4 w-4" />
                    {editingId ? "Spara ändringar" : "Spara spelare"}
                  </button>

                  {editingId && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#d8d2c4] bg-[#fffdf8] px-4 py-2.5 text-sm font-semibold text-[#68716c] transition hover:bg-[#f7f4ec]"
                    >
                      <XIcon className="h-4 w-4" />
                      Avbryt
                    </button>
                  )}
                </div>
              </div>
            </section>

            {/* Player list */}
            <section className="min-w-0 overflow-hidden rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_8px_24px_rgba(31,45,38,0.07)]">
              <div className="flex items-end justify-between gap-3 border-b border-[#e4ded1] px-4 py-4 sm:px-5">
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9a7735]">
                    Register
                  </p>

                  <h2 className="mt-0.5 text-lg font-semibold text-[#183d2d]">
                    Sparade spelare
                  </h2>
                </div>

                <span className="rounded-full bg-[#e5ede7] px-3 py-1.5 text-xs font-semibold text-[#183d2d]">
                  {players.length}
                </span>
              </div>

              {players.length === 0 ? (
                <div className="px-6 py-14 text-center">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#e5ede7] text-[#183d2d]">
                    <UsersIcon className="h-6 w-6" />
                  </span>

                  <h3 className="mt-4 text-lg font-semibold text-[#183d2d]">
                    Inga spelare ännu
                  </h3>

                  <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-[#68716c]">
                    Lägg till din första spelare i formuläret.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 p-3 sm:p-4 xl:grid-cols-2">
                  {players.map((player, index) => {
                    const isExpanded =
                      isDesktop || expandedPlayerId === player.id;
                    const imageUrl = player.image_url || player.image_yrl;

                    return (
                      <motion.article
                        key={player.id}
                        className="min-w-0 overflow-hidden rounded-xl border border-[#d8d2c4] bg-[#fffdf8] transition hover:border-[#b8b09f] hover:shadow-md"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.3,
                          delay: Math.min(index * 0.035, 0.2),
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (isDesktop) return;

                            setExpandedPlayerId((current) =>
                              current === player.id ? null : player.id
                            );
                          }}
                          className="flex w-full items-center gap-3 p-3 text-left sm:cursor-default"
                          aria-expanded={isExpanded}
                        >
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={player.name}
                              className="h-11 w-11 shrink-0 rounded-full border border-[#d8d2c4] object-cover"
                            />
                          ) : (
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e5ede7] text-sm font-bold text-[#183d2d]">
                              {getInitials(player.name)}
                            </span>
                          )}

                          <div className="min-w-0 flex-1">
                            <p className="break-words text-sm font-semibold leading-snug text-[#1f2d26]">
                              {player.name}
                            </p>

                            <p className="mt-0.5 truncate text-[11px] text-[#7b817d]">
                              {player.home_club || "Klubb saknas"}
                            </p>
                          </div>

                          <span className="shrink-0 rounded-full bg-[#f2ead9] px-2.5 py-1 text-xs font-bold tabular-nums text-[#9a7735]">
                            HCP {player.handicap}
                          </span>

                          <ChevronDownIcon
                            className={`h-4 w-4 shrink-0 text-[#8a8f8b] transition-transform sm:hidden ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {isExpanded && (
                          <div className="flex gap-2 border-t border-[#e4ded1] bg-[#f7f4ec] p-3">
                            <button
                              type="button"
                              onClick={() => handleEdit(player)}
                              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-[#cfd8d1] bg-[#fffdf8] px-3 py-2 text-xs font-semibold text-[#183d2d] transition hover:bg-[#eef3ef]"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                              Redigera
                            </button>

                            <button
                              type="button"
                              onClick={() => setDeleteCandidate(player)}
                              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-[#dfc5c0] bg-[#fff8f6] px-3 py-2 text-xs font-semibold text-[#a0443e] transition hover:bg-[#f7ebe8]"
                            >
                              <Trash2Icon className="h-3.5 w-3.5" />
                              Radera
                            </button>
                          </div>
                        )}
                      </motion.article>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </motion.div>
      </main>

      {/* Delete confirmation */}
      {deleteCandidate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#17231d]/60 px-4 backdrop-blur-sm">
          <motion.div
            className="w-full max-w-sm overflow-hidden rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-2xl"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-player-title"
          >
            <div className="border-b border-[#e4ded1] px-5 py-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f3e8e5] text-[#a0443e]">
                <Trash2Icon className="h-5 w-5" />
              </span>

              <h3
                id="delete-player-title"
                className="mt-3 text-lg font-semibold text-[#1f2d26]"
              >
                Radera spelare
              </h3>

              <p className="mt-2 text-sm leading-6 text-[#68716c]">
                Är du säker på att du vill radera{" "}
                <strong className="font-semibold text-[#1f2d26]">
                  {deleteCandidate.name}
                </strong>
                ?
              </p>
            </div>

            <div className="flex justify-end gap-2 bg-[#f7f4ec] px-5 py-4">
              <button
                type="button"
                onClick={() => setDeleteCandidate(null)}
                className="rounded-xl border border-[#d8d2c4] bg-[#fffdf8] px-4 py-2 text-sm font-semibold text-[#68716c] transition hover:bg-white"
              >
                Avbryt
              </button>

              <button
                type="button"
                onClick={confirmDelete}
                className="rounded-xl bg-[#a0443e] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#8e3934]"
              >
                Radera
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}